import { initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';

interface LeadAvatarProps {
  lead: { _id: string; profile?: { name?: string; uniqueId?: string }; gender?: string };
  className?: string;
  fallbackClassName?: string;
}

function avatarSeed(lead: LeadAvatarProps['lead']): string {
  return lead.profile?.uniqueId || lead._id;
}

function avatarGender(raw?: string): 'M' | 'F' | null {
  const value = raw?.trim().toUpperCase();
  if (value === 'F') return 'F';
  if (value === 'M') return 'M';
  return null;
}

const FEMALE_HAIR = 'full,pixie';
const MALE_HAIR = 'fonze,mrT,dougFunny,mrClean,dannyPhantom';

/** URL DiceBear Micah — seed estável; boca sorridente; cabelo/traços alinhados ao gênero. */
export function dicebearMicahUrl(lead: LeadAvatarProps['lead']): string {
  const seed = encodeURIComponent(avatarSeed(lead));
  const params = new URLSearchParams({
    seed,
    // Restringe a bocas positivas (Micah escolhe aleatório no seed se não filtrar).
    mouth: 'smile,laughing,smirk',
  });

  const gender = avatarGender(lead.gender);
  if (gender === 'F') {
    params.set('hair', FEMALE_HAIR);
    params.set('facialHairProbability', '0');
    params.set('earringsProbability', '80');
    params.set('eyebrows', 'eyelashesUp,eyelashesDown');
  } else if (gender === 'M') {
    params.set('hair', MALE_HAIR);
    params.set('facialHairProbability', '50');
    params.set('earringsProbability', '0');
    params.set('eyebrows', 'up,down');
  }

  return `https://api.dicebear.com/9.x/micah/svg?${params.toString()}`;
}

export function LeadAvatar({ lead, className, fallbackClassName }: LeadAvatarProps) {
  const name = lead.profile?.name ?? '?';
  return (
    <Avatar className={cn('shrink-0 bg-primary/15', className)}>
      <AvatarImage
        src={dicebearMicahUrl(lead)}
        alt={name}
        className="m-auto size-[88%] object-contain"
      />
      <AvatarFallback className={cn('bg-primary/15 font-semibold text-primary', fallbackClassName)}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
